/**
 * <PromptPreviewPanel> — lets a tenant try a system prompt against
 * argus_fast with their own variable values, then see the LLM output.
 *
 * Used inside the prompt detail sheet. Pure UI — calls POST
 * /api/v1/admin/discover/prompts/:slug/preview.
 */

import { useState, useMemo } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Play, AlertTriangle, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { apiClient } from '@/lib/api-client';

interface DeclaredVariable {
  name: string;
  label?: string;
  required?: boolean;
  default?: string;
  description?: string;
}

interface Props {
  slug: string;
  declaredVariables: DeclaredVariable[];
}

interface PreviewResponse {
  renderedPrompt: string;
  modelOutput: string;
  brand: string;
  latencyMs: number;
  tokensIn?: number;
  tokensOut?: number;
}

export function PromptPreviewPanel({ slug, declaredVariables }: Props) {
  const initialVars = useMemo(() => {
    const out: Record<string, string> = {};
    for (const v of declaredVariables) {
      out[v.name] = v.default ?? '';
    }
    return out;
  }, [declaredVariables]);

  const [values, setValues] = useState<Record<string, string>>(initialVars);

  const preview = useMutation({
    mutationFn: () =>
      apiClient.post<PreviewResponse>(
        `/admin/discover/prompts/${encodeURIComponent(slug)}/preview`,
        { variables: values },
      ),
  });

  return (
    <div className="space-y-3">
      <h4 className="text-sm font-medium flex items-center gap-1.5">
        <Sparkles className="h-3.5 w-3.5 text-muted-foreground" />
        Preview against argus_fast
      </h4>
      <p className="text-xs text-muted-foreground">
        Fill in sample variables then press Run to see what this prompt would
        actually generate. Uses the fast brand (8B-class, hot, low latency).
      </p>

      {declaredVariables.length === 0 ? (
        <p className="text-[11px] italic text-muted-foreground">
          This prompt declares no variables — Run it as-is.
        </p>
      ) : (
        <div className="space-y-2">
          {declaredVariables.map((v) => (
            <div key={v.name} className="space-y-1">
              <Label htmlFor={`prompt-var-${v.name}`} className="text-[11px]">
                {v.label ?? v.name}
                {v.required && <span className="text-destructive ml-1">*</span>}
                {v.description && (
                  <span className="ml-1 font-normal text-muted-foreground">
                    — {v.description}
                  </span>
                )}
              </Label>
              <Input
                id={`prompt-var-${v.name}`}
                value={values[v.name] ?? ''}
                onChange={(e) =>
                  setValues((prev) => ({ ...prev, [v.name]: e.target.value }))
                }
                className="h-8 text-sm"
                placeholder={v.default ?? ''}
              />
            </div>
          ))}
        </div>
      )}

      <Button
        type="button"
        size="sm"
        onClick={() => preview.mutate()}
        disabled={preview.isPending}
      >
        <Play className="h-3 w-3 mr-1.5" />
        {preview.isPending ? 'Running...' : 'Run preview'}
      </Button>

      {preview.error && (
        <div className="border border-destructive/30 bg-destructive/5 rounded p-2 text-xs text-destructive flex items-start gap-1.5">
          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <div>
            {preview.error instanceof Error
              ? preview.error.message
              : String(preview.error)}
          </div>
        </div>
      )}

      {preview.data && (
        <div className="space-y-2">
          <div className="space-y-1">
            <div className="text-[11px] text-muted-foreground">
              Rendered prompt (sent to {preview.data.brand}):
            </div>
            <pre className="text-[11px] bg-muted p-2 rounded whitespace-pre-wrap break-words">
              {preview.data.renderedPrompt}
            </pre>
          </div>
          <div className="space-y-1">
            <div className="text-[11px] text-muted-foreground flex items-center gap-2">
              <span>Model output:</span>
              <span className="font-mono">
                {preview.data.latencyMs}ms
                {preview.data.tokensOut !== undefined &&
                  ` · ${preview.data.tokensOut} tokens out`}
              </span>
            </div>
            <pre className="text-xs bg-emerald-500/5 border border-emerald-500/20 p-2 rounded whitespace-pre-wrap break-words">
              {preview.data.modelOutput}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
}
